import { HTMLAttributes } from 'preact/compat';
import { MutableRef, useEffect, useRef, useState } from 'preact/hooks';

import { FallbackImage } from 'components/FallbackImage';

interface LazyImageProps {
  fallback?: string;
  wrapperRef: MutableRef<HTMLElement | null>;
  onLoad?: () => void;
}

export function LazyImage({
  src,
  fallback,
  wrapperRef,
  onLoad,
  ...props
}: HTMLAttributes<HTMLImageElement> & LazyImageProps) {
  const [visible, setVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (visible) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        const { isIntersecting } = entry;

        setVisible(isIntersecting);
      },
      { threshold: 0, rootMargin: '200px' },
    );

    const observer = observerRef.current;
    const wrapper = wrapperRef.current;

    if (observer && wrapper) {
      observer.observe(wrapper);
    }

    return () => {
      if (observer && wrapper) {
        observer.unobserve(wrapper);
        observer.disconnect();
      }
    };
  }, [visible, wrapperRef]);

  const imageMarkup = (
    <FallbackImage src={src} fallback={fallback} onLoad={onLoad} {...props} />
  );

  return visible ? imageMarkup : null;
}
